// Eigentümerportal E7: Rechnungen, Termine, Dokumente, Finanzen (Soll) – Datentypen und reine Regeln.
// Finanzen nur aus gespeicherten Soll-Werten (heutige Mieten, Hausgeld-Vorschuss). Keine Monatskurve: die Portal-
// Funktion liefert nur laufende Mietverhältnisse, frühere Monate wären bei Mieterwechseln zu niedrig.
import { gesamtmieteAm } from '../mietkonto/soll';
import { istWohnend } from '../dashboard/leerstand';
import type { EigDaten, ObjektSicht } from './eigentuemer';

// ── Rechnungen ──
export type RechnungStand = 'offen' | 'freigegeben' | 'abgelehnt';

export interface PortalRechnung {
  id: string; objekt_id: string | null; rechnungsnummer: string | null; unternehmen: string | null; bezeichnung: string | null;
  brutto: number | string | null; datum: string | null; einheit: string | null; stand: RechnungStand;
  entschieden_am: string | null; begruendung: string | null; beleg_id: string | null;
}

export const MIN_BEGRUENDUNG = 5;
export const begruendungOk = (text: string): boolean => text.trim().length >= MIN_BEGRUENDUNG;

export type RechnungFilter = 'offen' | 'erledigt';
export const rechnungenFuer = (alle: readonly PortalRechnung[], objektId: string, f: RechnungFilter): PortalRechnung[] =>
  alle.filter((r) => r.objekt_id === objektId && (f === 'offen' ? r.stand === 'offen' : r.stand !== 'offen'));

export const offeneAnzahl = (alle: readonly PortalRechnung[], objektId: string): number =>
  alle.filter((r) => r.objekt_id === objektId && r.stand === 'offen').length;

const DATUM = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
export const euro = (n: number): string => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
export const betrag = (r: Pick<PortalRechnung, 'brutto'>): number => Number(r.brutto) || 0;

export function rechnungUnterzeile(r: PortalRechnung): string {
  return [r.unternehmen, r.rechnungsnummer].map((x) => (x ?? '').trim()).filter(Boolean).join(' · ');
}

export function rechnungOrt(r: PortalRechnung): string[] {
  return [r.einheit ?? '', r.datum ? `Rechnung vom ${DATUM(r.datum)}` : ''].filter(Boolean);
}

/** Text unter einer erledigten Rechnung. */
export function erledigtText(r: PortalRechnung): string {
  const am = r.entschieden_am ? ` am ${DATUM(r.entschieden_am)}` : '';
  return r.stand === 'abgelehnt' ? `Von Ihnen abgelehnt${am}` : `Von Ihnen freigegeben${am}`;
}

/** Büro, Rechnungs-Detail: Was zeigt der Eigentümer-Hinweis? null = nichts (keine Wertgrenze, darunter, bezahlt ohne Entscheidung). */
export function entscheidungsLage(s: { status: string | null; brutto: number | string | null; eigentuemer_entscheidung: 'freigegeben' | 'abgelehnt' | null; grenze: number | string | null }): 'wartet' | 'freigegeben' | 'abgelehnt' | null {
  if (s.eigentuemer_entscheidung) return s.eigentuemer_entscheidung;
  if ((s.status ?? '').toLowerCase() === 'bezahlt' || s.grenze === null || s.grenze === undefined) return null;
  return Number(s.brutto) > Number(s.grenze) ? 'wartet' : null;
}

// ── Termine und Dokumente ──
export interface EigTermin {
  id: string; objekt_id: string | null; titel: string; typ: string; typ_freitext: string | null;
  start_datum: string; end_datum: string | null; ganztaegig: boolean | null; firma: string | null;
}

export interface EigDokument {
  id: string; objekt_id: string | null; titel: string; datei_name: string | null; kategorie: string | null;
  dokument_typ: string | null; quelle: string | null; created_at: string; datei_groesse: number | null;
  datei_typ: string | null; herunterladbar: boolean;
}

/** Dokumente zum Objekt; persönliche Unterlagen (ohne Objekt) erscheinen bei jedem Objekt. */
export const fuerObjekt = <T extends { objekt_id: string | null }>(liste: readonly T[], objektId: string): T[] =>
  liste.filter((x) => x.objekt_id === objektId || x.objekt_id === null);

export type EigDokGruppe = 'versammlung' | 'abrechnungen' | 'grundlagen' | 'weitere';
export const EIG_DOK_GRUPPEN: { key: EigDokGruppe; label: string }[] = [
  { key: 'versammlung', label: 'Versammlung' },
  { key: 'abrechnungen', label: 'Abrechnungen' },
  { key: 'grundlagen', label: 'Grundlagen' },
  { key: 'weitere', label: 'Weitere Unterlagen' },
];

/** Gruppe aus Kategorie/Typ (im Bestand Freitext). */
export function eigDokGruppe(d: Pick<EigDokument, 'kategorie' | 'dokument_typ' | 'titel'>): EigDokGruppe {
  const k = `${d.kategorie ?? ''} ${d.titel ?? ''}`.toLowerCase();
  if (/versammlung|etv|einladung|beschluss|protokoll|tagesordnung|vollmacht/.test(k)) return 'versammlung';
  if (/abrechnung|wirtschaftsplan|hausgeld|rücklage|ruecklage/.test(k)) return 'abrechnungen';
  if (d.dokument_typ === 'vertrag' || /vertrag|teilungserkl|gemeinschaftsordnung|grundbuch|energieausweis|versicherung/.test(k)) return 'grundlagen';
  return 'weitere';
}

export function gruppiereEigDokumente(docs: EigDokument[]): { key: EigDokGruppe; label: string; docs: EigDokument[] }[] {
  return EIG_DOK_GRUPPEN.map((g) => ({ ...g, docs: docs.filter((d) => eigDokGruppe(d) === g.key) })).filter((g) => g.docs.length > 0);
}

// ── Finanzen (Soll) ──
const r2 = (n: number) => Math.round(n * 100) / 100;

export interface EinheitSoll { bezeichnung: string; mieter: string | null; kalt: number; gesamt: number }

/** Heutige Monatsmiete je Einheit (Kalt und Gesamt). */
export function sollJeEinheit(d: EigDaten, s: ObjektSicht, heute: string): EinheitSoll[] {
  if (!s.mieterSichtbar) return [];
  return s.einheiten.map((e) => {
    const laufend = d.mieter.filter((m) => m.einheit_id === e.id && istWohnend(m.status)
      && !(m.mietbeginn && m.mietbeginn.slice(0, 10) > heute) && !(m.mietende && m.mietende.slice(0, 10) < heute));
    return {
      bezeichnung: e.bezeichnung, mieter: e.mieterName, kalt: e.kaltmiete,
      gesamt: r2(laufend.reduce((sum, m) => sum + gesamtmieteAm({ ...m, id: '' }, heute), 0)),
    };
  });
}

export interface HausgeldTeil { label: string; betrag: number }

/** Monatliches Hausgeld der eigenen Einheiten (Vorschuss + Rücklagenzuführung). */
export function hausgeldTeile(d: EigDaten, s: ObjektSicht): HausgeldTeil[] {
  const ids = new Set(s.einheiten.map((e) => e.id));
  const eig = d.einheit_eig.filter((x) => ids.has(x.einheit_id));
  const z = (v: unknown) => Number(v) || 0;
  return [
    { label: 'Hausgeld-Vorschuss', betrag: r2(eig.reduce((sum, x) => sum + z(x.hausgeld_vorschuss), 0)) },
    { label: 'Zuführung Erhaltungsrücklage', betrag: r2(eig.reduce((sum, x) => sum + z(x.ruecklage_vorschuss), 0)) },
  ].filter((t) => t.betrag > 0);
}
